import React from 'react'
import PrefillEditFieldsEditor from './PrefillEditFieldsEditor'
import {
  defaultColumnHeaders,
  getColumnTypes,
  getTableDimensions,
  resizeHeaders
} from './tableFieldUtils'
import {
  PrefillEditFieldDefinition,
  TableRowPrefillSettings,
  getTableRowPrefillEditFields,
  getTableRowPrefillSettings
} from './prefillFieldSettingsUtils'

interface Props {
  element: any
  rowIndex: number
  rowLabel: string
  onUpdateRowSettings: (rowIndex: number, updates: Partial<TableRowPrefillSettings>) => void
}

const TableRowPrefillConfigPanel: React.FC<Props> = ({
  element,
  rowIndex,
  rowLabel,
  onUpdateRowSettings
}) => {
  const { cols } = getTableDimensions(element)
  const columnHeaders = resizeHeaders(element.columnHeaders, cols, defaultColumnHeaders)
  const columnTypes = getColumnTypes(element)
  const rowConfig = getTableRowPrefillSettings(element, rowIndex)
  const editFields = getTableRowPrefillEditFields(element, rowIndex)

  const toggleEditableColumn = (columnIndex: number) => {
    const selected = rowConfig.editableColumnIndexes || []
    const next = selected.includes(columnIndex)
      ? selected.filter((index) => index !== columnIndex)
      : [...selected, columnIndex].sort((a, b) => a - b)
    onUpdateRowSettings(rowIndex, {
      allowEdit: true,
      editableColumnIndexes: next,
      prefillEditFields: rowConfig.prefillEditFields || []
    })
  }

  return (
    <div className='border border-warning border-2 rounded p-3 mt-2 mb-2 bg-warning bg-opacity-10'>
      <h6 className='text-warning-emphasis mb-2'>
        Edit options — {rowLabel || `Row ${rowIndex + 1}`}
      </h6>

      <div className='form-check form-switch mb-3'>
        <input
          className='form-check-input'
          type='checkbox'
          id={`table-row-allow-edit-${element.fieldId}-${rowIndex}`}
          checked={rowConfig.allowEdit === true}
          onChange={(e) =>
            onUpdateRowSettings(rowIndex, {
              allowEdit: e.target.checked,
              editableColumnIndexes: rowConfig.editableColumnIndexes || [],
              prefillEditFields: rowConfig.prefillEditFields || []
            })
          }
        />
        <label
          className='form-check-label'
          htmlFor={`table-row-allow-edit-${element.fieldId}-${rowIndex}`}
        >
          Allow edit for this row
        </label>
      </div>

      {rowConfig.allowEdit ? (
        <>
          <div className='mb-3'>
            <label className='form-label fw-semibold'>Editable columns</label>
            <p className='text-muted small mb-2'>
              Choose which columns the recipient can edit. Leave all unchecked to allow every
              column.
            </p>
            <div className='d-flex flex-column gap-2'>
              {columnHeaders.map((header, columnIndex) => {
                const selectedColumns = rowConfig.editableColumnIndexes || []
                const isChecked =
                  selectedColumns.length > 0 && selectedColumns.includes(columnIndex)
                return (
                  <div key={`row-col-edit-${rowIndex}-${columnIndex}`} className='form-check'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      id={`row-${rowIndex}-col-${columnIndex}`}
                      checked={isChecked}
                      onChange={() => toggleEditableColumn(columnIndex)}
                    />
                    <label
                      className='form-check-label'
                      htmlFor={`row-${rowIndex}-col-${columnIndex}`}
                    >
                      {header} <span className='text-muted'>({columnTypes[columnIndex]})</span>
                    </label>
                  </div>
                )
              })}
            </div>
          </div>

          <div>
            <label className='form-label fw-semibold'>Conditional edit fields</label>
            <p className='text-muted small mb-2'>
              Extra fields shown below this row when the recipient edits it on the public form.
            </p>
            <PrefillEditFieldsEditor
              parentFieldId={element.fieldId}
              editFields={editFields}
              rowIndex={rowIndex}
              onChange={(nextEditFields: PrefillEditFieldDefinition[]) =>
                onUpdateRowSettings(rowIndex, {
                  allowEdit: true,
                  editableColumnIndexes: rowConfig.editableColumnIndexes || [],
                  prefillEditFields: nextEditFields
                })
              }
            />
          </div>
        </>
      ) : null}
    </div>
  )
}

export default TableRowPrefillConfigPanel
